/**
 * Admin auth client.
 *
 * The API gates all `/api` traffic behind a single admin credential pair. After
 * a successful login the server issues an opaque session token (returned in the
 * JSON body and mirrored to an httpOnly cookie). We keep our own copy in
 * localStorage so we can:
 *   - attach it as `Authorization: Bearer <token>` on every GraphQL request
 *     (works cross-origin, where the httpOnly cookie would not be sent), and
 *   - append it as `?instabot_admin=<token>` to media URLs and the WS URL.
 *
 * In the test stacks the API runs with `INSTABOT_DISABLE_AUTH=1`, so login is a
 * no-op and no token is ever stored — existing UI/API suites keep working.
 */
import axios from "axios";
import { apiHttpUrl } from "./api-base";

const TOKEN_KEY = "instabot_admin_token";

export function getAuthToken(): string | null {
  try {
    return window.localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setAuthToken(token: string | null): void {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* storage unavailable — auth simply won't persist */
  }
}

export async function adminLogin(
  username: string,
  password: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await axios.post(
      apiHttpUrl("/api/admin/login"),
      { username, password },
      {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      },
    );
    const body = res.data as { ok?: boolean; token?: string; disabled?: boolean };
    if (body?.ok && body.token) {
      setAuthToken(body.token);
    }
    return { ok: true };
  } catch (err) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    return {
      ok: false,
      error:
        status === 401
          ? "Invalid username or password"
          : "Could not reach the server",
    };
  }
}

/**
 * Ask the server whether the stored token (or cookie) is currently valid.
 * Returns "unknown" on a network error (server not ready yet) so callers can
 * retry instead of wrongly kicking the user to the login screen.
 */
export async function adminCheckAuth(): Promise<"authed" | "unauthed" | "unknown"> {
  const token = getAuthToken();
  try {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await axios.get(apiHttpUrl("/api/admin/me"), {
      headers,
      withCredentials: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    return res.status === 200 ? "authed" : "unauthed";
  } catch {
    return "unknown";
  }
}

export async function adminLogout(): Promise<void> {
  const token = getAuthToken();
  try {
    const headers: Record<string, string> = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    await axios.post(apiHttpUrl("/api/admin/logout"), {}, {
      headers,
      withCredentials: true,
    });
  } catch {
    /* best-effort — we clear the local token regardless */
  }
  setAuthToken(null);
}

/**
 * Append the session token to an API URL as a query param, for requests that
 * cannot carry an Authorization header (<img>/<video> src, WebSocket connect).
 */
export function withAuthToken(url: string): string {
  const token = getAuthToken();
  if (!token) return url;
  try {
    const u = new URL(url, window.location.origin);
    if (u.searchParams.get("instabot_admin")) return url;
    u.searchParams.set("instabot_admin", token);
    return u.toString();
  } catch {
    // Relative URL without a usable base — fall back to string concat.
    const sep = url.includes("?") ? "&" : "?";
    return `${url}${sep}instabot_admin=${encodeURIComponent(token)}`;
  }
}
